/**
 * Validate email format
 */
export const validateEmail = (email: string): string | null => {
  if (!email || email.trim() === '') {
    return 'Email is required';
  }

  if (email.length > 254) {
    return 'Email is too long (max 254 characters)';
  }

  // Basic email regex
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    return 'Please enter a valid email address';
  }

  return null;
};

/**
 * Validate password
 */
export const validatePassword = (password: string): string | null => {
  if (!password || password.trim() === '') {
    return 'Password is required';
  }

  return null;
};

/**
 * Validate 2FA code
 */
export const validate2FACode = (code: string): string | null => {
  if (!code || code.trim() === '') {
    return 'Please enter code';
  }

  if (code.length < 5) {
    return 'Please enter a valid code';
  }

  return null;
};

/**
 * Validate organization name
 */
export const validateOrganization = (orgName: string): string | null => {
  if (!orgName || orgName.trim() === '') {
    return "Please, enter your organization's name";
  }

  if (orgName.length > 254) {
    return 'Organization name is too long';
  }

  return null;
};

/**
 * Validate that an organization is selected from the list
 */
export const validateOrganizationSelected = (isSelected: boolean): string | null => {
  if (!isSelected) {
    return 'Please, choose your organization from the list';
  }

  return null;
};

/**
 * Validate password strength with detailed requirements
 * Returns validation result with strength percentage and specific error
 */
export interface PasswordValidationResult {
  error: string | null;
  strength: number; // 0-100
  isValid: boolean;
}

export const validatePasswordStrength = (password: string): PasswordValidationResult => {
  const validated = {
    hasMinLength: password.length >= 8,
    hasDigit: /\d/.test(password),
    hasUpperCase: /[A-Z]/.test(password),
    hasSpecialCharacter: /[^A-Za-z\d]/.test(password),
  };

  let error: string | null = null;
  let strength = 0;

  if (validated.hasMinLength) strength += 25;
  if (validated.hasDigit) strength += 25;
  if (validated.hasUpperCase) strength += 25;
  if (validated.hasSpecialCharacter) strength += 25;

  if (!validated.hasMinLength) {
    error = 'Password must contain at least 8 characters';
  } else if (!validated.hasDigit) {
    error = 'Password must contain at least 1 digit';
  } else if (!validated.hasUpperCase) {
    error = 'Password must contain at least 1 uppercase letter';
  } else if (!validated.hasSpecialCharacter) {
    error = 'Password must contain at least 1 special character';
  }

  return {
    error,
    strength,
    isValid: strength === 100 && !error,
  };
};
